/**
 * bandit.ts — the binary pickup/skip classifier (v2 reframe).
 *
 * Ported from `_proto_kitchen_v2.py` `KitchenBandit` + `TreeHead` +
 * `LinearHead`. The v1 three-role MoE (gate → exploiter → navigator) is
 * GONE: movement is a fixed BFS walk owned by the env, and the only
 * learned decision is binary {PICKUP, SKIP} per encountered item.
 *
 * Routing (per item):
 *   1. random arm                                → coin flip.
 *   2. drift re-explore window                   → forced coin flip.
 *   3. cold start (n_observed < coldStartK)      → coin flip.
 *   4. model-driven: score both arms, PICKUP iff predicted(PICKUP) >
 *      predicted(SKIP). For 'ts', Thompson-sample one tree per arm first.
 *
 * The arms differ ONLY in the model class:
 *   ts/greedy → TreeHead (ExtraTrees), linear → LinearHead (ridge foil),
 *   random → coin flip (never uses the head).
 *
 * Counterfactual training (the key v2 trick): runEpisode calls observe()
 * TWICE per decision — once per arm — with the rewards BOTH arms would
 * have yielded (skip → 0; pickup → basket-score delta). The head sees
 * balanced 2-arm data and can contrast the arms directly. The drift
 * detector is fed only on the arm actually taken (takenAction == a).
 */
import {
  N_ACTIONS, N_FEATURES, SKIP, PICKUP,
  WINDOW_PER_DAY, LONG_CAP, N_TREES, COLD_START_K, REFIT_EVERY, RIDGE_LAMBDA,
  type ArmMode, type DriftStatus,
} from './constants';
import { ExtraTreesForest } from './forest';
import { EWMADriftDetector } from './drift';
import type { DriftHyperparams } from './drift';
import { makeRng } from './linalg';

// ── Hyperparams (interface kept name-compatible with worker.ts) ──────────
export interface BanditHyperparams {
  mode: ArmMode;
  nTrees: number;
  minSamplesLeaf: number;
  windowPerDay: number;
  longCap: number;
  coldStartK: number;
  refitEvery: number;
  useDriftDetector: boolean;
  drift: DriftHyperparams;
  ridgeLambda: number;
  seed: number;
}

export const DEFAULT_BANDIT_HP: BanditHyperparams = {
  mode: 'ts',
  nTrees: N_TREES,
  minSamplesLeaf: 3,
  windowPerDay: WINDOW_PER_DAY,
  longCap: LONG_CAP,
  coldStartK: COLD_START_K,
  refitEvery: REFIT_EVERY,
  useDriftDetector: true,
  drift: { lam: 0.05, lWarning: 2.0, lDrift: 3.0, warmup: 8, exploreWindow: 6 },
  ridgeLambda: RIDGE_LAMBDA,
  seed: 0,
};

export interface SelectResult {
  action: number;        // SKIP (0) or PICKUP (1)
  mean: Float64Array;    // per-arm predicted reward
  std: Float64Array;     // per-arm across-tree std
  /** Why the action was chosen. */
  reason: 'drift' | 'cold' | 'model';
}

export interface ObserveResult {
  refitRequested: boolean;
  driftStatus: 'warmup' | 'ok' | 'warning' | 'drift' | 'off';
}

export type FitFn = (
  X: Float64Array,
  y: Float64Array,
  hp: BanditHyperparams,
) => void;

// ── Linear foil head (ridge closed-form) ─────────────────────────────────
// Same 20 features as TreeHead, but the dot product w·x has no item×ctx
// cross term. On the all_day theme it collapses: its best estimate of each
// flip item's value is the average across contexts (~0), so it defaults to
// picking up the 4 stable items and indiscriminately flipping on the
// context-flip items. The negative result that proves non-linearity load.
class LinearHead {
  w: Float64Array | null = null;
  b = 0;
  constructor(private lam: number) {}
  get isFit(): boolean { return this.w !== null; }
  fit(X: Float64Array, y: Float64Array): void {
    const n = y.length;
    const d = N_FEATURES;
    // Build [X | 1] design matrix.
    const Xb = new Float64Array(n * (d + 1));
    for (let i = 0; i < n; i++) {
      Xb.set(X.subarray(i * d, (i + 1) * d), i * (d + 1));
      Xb[i * (d + 1) + d] = 1;
    }
    // A = Xb^T Xb + λI, b = Xb^T y. Solve via Gaussian elimination.
    const A = new Float64Array((d + 1) * (d + 1));
    const rhs = new Float64Array(d + 1);
    for (let i = 0; i < n; i++) {
      const row = Xb.subarray(i * (d + 1), (i + 1) * (d + 1));
      for (let a = 0; a <= d; a++) {
        rhs[a] += row[a] * y[i];
        for (let b = 0; b <= d; b++) {
          A[a * (d + 1) + b] += row[a] * row[b];
        }
      }
    }
    for (let a = 0; a <= d; a++) A[a * (d + 1) + a] += this.lam;
    const sol = solveLinear(A, rhs, d + 1);
    this.w = sol.subarray(0, d);
    this.b = sol[d];
  }
  predictAllActions(X_all: Float64Array): { mean: Float64Array; std: Float64Array } {
    const mean = new Float64Array(N_ACTIONS);
    const std = new Float64Array(N_ACTIONS);
    std.fill(1);
    if (this.w === null) return { mean, std };
    for (let a = 0; a < N_ACTIONS; a++) {
      const off = a * N_FEATURES;
      let s = this.b;
      for (let i = 0; i < N_FEATURES; i++) s += this.w[i] * X_all[off + i];
      mean[a] = s;
    }
    return { mean, std };
  }
  reset(): void { this.w = null; this.b = 0; }
}

/** Solve Ax = b via Gaussian elimination with partial pivoting. */
function solveLinear(A: Float64Array, b: Float64Array, n: number): Float64Array {
  const M = new Float64Array(n * (n + 1));
  for (let i = 0; i < n; i++) {
    M.set(A.subarray(i * n, (i + 1) * n), i * (n + 1));
    M[i * (n + 1) + n] = b[i];
  }
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r * (n + 1) + col]) > Math.abs(M[piv * (n + 1) + col])) piv = r;
    }
    if (piv !== col) {
      const tmp = M.subarray(piv * (n + 1), (piv + 1) * (n + 1)).slice();
      M.copyWithin(piv * (n + 1), col * (n + 1), (col + 1) * (n + 1));
      M.set(tmp, col * (n + 1));
    }
    const diag = M[col * (n + 1) + col];
    if (Math.abs(diag) < 1e-12) continue;
    for (let r = col + 1; r < n; r++) {
      const factor = M[r * (n + 1) + col] / diag;
      if (factor === 0) continue;
      for (let c = col; c <= n; c++) {
        M[r * (n + 1) + c] -= factor * M[col * (n + 1) + c];
      }
    }
  }
  const x = new Float64Array(n);
  for (let r = n - 1; r >= 0; r--) {
    let s = M[r * (n + 1) + n];
    for (let c = r + 1; c < n; c++) s -= M[r * (n + 1) + c] * x[c];
    const diag = M[r * (n + 1) + r];
    x[r] = Math.abs(diag) < 1e-12 ? 0 : s / diag;
  }
  return x;
}

// ── The agent (binary pickup/skip classifier) ────────────────────────────
export class KitchenBandit {
  hp: BanditHyperparams;
  forest: ExtraTreesForest;          // ts/greedy head
  private linear: LinearHead;        // linear foil head
  drift: EWMADriftDetector;
  private rng: () => number;

  private XDay: Float64Array[] = [];
  private yDay: number[] = [];
  private XLong: Float64Array[] = [];
  private yLong: number[] = [];

  nObserved = 0;
  evalMode = false;
  private lastPred = 0;
  private lastReason: SelectResult['reason'] = 'cold';
  driftEvents: Array<{ n: number; status: DriftStatus; err: number }> = [];

  get dayBufferSize(): number { return this.XDay.length; }
  get longBufferSize(): number { return this.XLong.length; }
  get headIsFit(): boolean {
    return this.hp.mode === 'linear' ? this.linear.isFit : this.forest.isFit;
  }
  get lastSelectReason(): SelectResult['reason'] { return this.lastReason; }

  fitFn: FitFn;

  constructor(hp: Partial<BanditHyperparams> = {}) {
    this.hp = { ...DEFAULT_BANDIT_HP, ...hp };
    this.forest = new ExtraTreesForest({
      nTrees: this.hp.nTrees,
      minSamplesLeaf: this.hp.minSamplesLeaf,
      nFeatures: N_FEATURES,
      seed: this.hp.seed,
    });
    this.linear = new LinearHead(this.hp.ridgeLambda);
    this.drift = new EWMADriftDetector(this.hp.drift);
    this.rng = makeRng(this.hp.seed ^ 0x9e3779b9);
    this.fitFn = (X, y, h) => {
      // Default synchronous fit (smoketest / harness). UI overrides to post
      // to the worker (ForestClient.install).
      if (h.mode === 'linear') {
        this.linear.fit(X, y);
      } else {
        this.forest.hp.nTrees = h.nTrees;
        this.forest.hp.minSamplesLeaf = h.minSamplesLeaf;
        this.forest.hp.seed = h.seed;
        this.forest.fitSync(X, y);
      }
    };
  }

  resetDay(): void {
    this.XDay = [];
    this.yDay = [];
  }

  resetRun(): void {
    this.XDay = [];
    this.yDay = [];
    this.XLong = [];
    this.yLong = [];
    this.nObserved = 0;
    this.lastPred = 0;
    this.driftEvents = [];
    this.drift.resetRun();
    this.forest = new ExtraTreesForest({
      nTrees: this.hp.nTrees,
      minSamplesLeaf: this.hp.minSamplesLeaf,
      nFeatures: N_FEATURES,
      seed: this.hp.seed,
    });
    this.linear.reset();
  }

  /** Seed the long-term buffer with teacher (features, reward) rows. */
  pretrain(X: Float64Array, y: Float64Array): void {
    for (let i = 0; i < y.length; i++) {
      const row = X.subarray(i * N_FEATURES, (i + 1) * N_FEATURES).slice();
      this.XLong.push(row);
      this.yLong.push(y[i]);
    }
    // Immediate fit so the head is usable from step 1; bypass cold-start.
    const { X: cx, y: cy } = this.combinedDataset();
    if (cy.length >= N_ACTIONS) {
      this.fitFn(cx, cy, this.hp);
      this.nObserved = Math.max(this.hp.coldStartK + 1, y.length);
    }
  }

  combinedDataset(): { X: Float64Array; y: Float64Array } {
    const nDay = this.XDay.length;
    const nLong = this.XLong.length;
    const n = nDay + nLong;
    const X = new Float64Array(n * N_FEATURES);
    const y = new Float64Array(n);
    for (let i = 0; i < nDay; i++) {
      X.set(this.XDay[i], i * N_FEATURES);
      y[i] = this.yDay[i];
    }
    for (let i = 0; i < nLong; i++) {
      X.set(this.XLong[i], (nDay + i) * N_FEATURES);
      y[nDay + i] = this.yLong[i];
    }
    return { X, y };
  }

  private refit(): void {
    const { X, y } = this.combinedDataset();
    if (y.length < N_ACTIONS) return;
    this.fitFn(X, y, this.hp);
  }

  /** Binary select. X_all: (N_ACTIONS, N_FEATURES) row-major. Returns PICKUP
   *  or SKIP. `legal` is an optional N_ACTIONS mask (1=legal); illegal
   *  actions are removed from consideration BEFORE any model / drift / cold-
   *  start path. Used to enforce "PICKUP illegal when basket[item]=1" — a
   *  hard constraint, not a learned preference. */
  select(X_all: Float64Array, legal?: Uint8Array): SelectResult {
    const zero = new Float64Array(N_ACTIONS);
    const one = new Float64Array(N_ACTIONS).fill(1);
    const mask = legal ?? new Uint8Array(N_ACTIONS).fill(1);
    // Build the list of legal action indices. With only one legal action,
    // take it without consulting the model — the decision is forced and the
    // taken-action prediction carries no signal for drift.
    const legalIdx: number[] = [];
    for (let a = 0; a < N_ACTIONS; a++) if (mask[a]) legalIdx.push(a);
    if (legalIdx.length === 1) {
      this.lastPred = 0;
      this.lastReason = 'cold';
      return { action: legalIdx[0], mean: zero, std: one, reason: 'cold' };
    }
    // Stochastic path: uniform over LEGAL actions.
    const pickRandomLegal = (): number => {
      const j = Math.floor(this.rng() * legalIdx.length);
      return legalIdx[j];
    };
    // random arm: never consults the model.
    if (this.hp.mode === 'random') {
      this.lastPred = 0;
      this.lastReason = 'cold';
      return { action: pickRandomLegal(), mean: zero, std: one, reason: 'cold' };
    }
    // Layer 1: forced re-explore after a detected drift.
    if (this.hp.useDriftDetector && this.drift.shouldExplore()) {
      this.lastPred = 0;
      this.lastReason = 'drift';
      return { action: pickRandomLegal(), mean: zero, std: one, reason: 'drift' };
    }
    // Layer 2: cold start.
    if (this.nObserved < this.hp.coldStartK || !this.headIsFit) {
      this.lastPred = 0;
      this.lastReason = 'cold';
      return { action: pickRandomLegal(), mean: zero, std: one, reason: 'cold' };
    }
    // Layer 3: model-driven.
    const isTree = this.hp.mode !== 'linear';
    const { mean, std } = isTree
      ? this.forest.predictAllActions(X_all, N_ACTIONS)
      : this.linear.predictAllActions(X_all);
    let action: number;
    if (this.hp.mode === 'ts') {
      // Thompson-sample: one tree per arm, act on the sample.
      const treePicks = new Int32Array(N_ACTIONS);
      for (let a = 0; a < N_ACTIONS; a++) {
        treePicks[a] = Math.floor(this.rng() * this.hp.nTrees);
      }
      const samples = this.forest.sampleOneTreePrediction(X_all, N_ACTIONS, treePicks);
      // Mask illegal arms to -Inf so a sampled tree cannot resurrect them.
      const sSkip = mask[SKIP] ? samples[SKIP] : -Infinity;
      const sPick = mask[PICKUP] ? samples[PICKUP] : -Infinity;
      action = sPick > sSkip ? PICKUP : SKIP;
    } else {
      // greedy / linear: argmax over the ensemble mean (or the linear Q),
      // masked to legal actions.
      const mSkip = mask[SKIP] ? mean[SKIP] : -Infinity;
      const mPick = mask[PICKUP] ? mean[PICKUP] : -Infinity;
      action = mPick > mSkip ? PICKUP : SKIP;
    }
    this.lastPred = mean[action];
    this.lastReason = 'model';
    return { action, mean, std, reason: 'model' };
  }

  /** Observe one (arm, reward) counterfactual pair. runEpisode calls this
   *  TWICE per decision (once per arm); `takenAction` marks which arm was
   *  actually taken so the drift detector counts it exactly once. */
  observe(
    x: Float64Array,
    action: number,
    reward: number,
    takenAction: number = action,
    takenReward: number = reward,
  ): ObserveResult {
    if (this.evalMode) {
      if (takenAction === action) this.lastPred = 0;
      return { refitRequested: false, driftStatus: 'off' };
    }
    // Drift detector: only on the arm actually taken (not both).
    let status: 'warmup' | 'ok' | 'warning' | 'drift' = 'ok';
    let refitRequested = false;
    if (takenAction === action) {
      this.nObserved += 1;
      if (this.hp.useDriftDetector) {
        status = this.drift.update(this.lastPred, takenReward);
        if (status === 'warning' || status === 'drift') {
          this.driftEvents.push({
            n: this.nObserved, status, err: Math.abs(takenReward - this.lastPred),
          });
          if (status === 'drift') this.refit();
        }
      }
      this.lastPred = 0;
    }
    // Train on BOTH arms counterfactually (balanced 2-arm data).
    this.XDay.push(x.slice());
    this.yDay.push(reward);
    this.XLong.push(x.slice());
    this.yLong.push(reward);
    if (this.XDay.length > this.hp.windowPerDay) {
      const over = this.XDay.length - this.hp.windowPerDay;
      this.XDay.splice(0, over);
      this.yDay.splice(0, over);
    }
    if (this.XLong.length > this.hp.longCap) {
      const over = this.XLong.length - this.hp.longCap;
      this.XLong.splice(0, over);
      this.yLong.splice(0, over);
    }
    // Refit cadence counts DECISIONS (taken actions), not observations.
    if (takenAction === action && this.nObserved % this.hp.refitEvery === 0) {
      this.refit();
      refitRequested = true;
    }
    return { refitRequested, driftStatus: this.hp.useDriftDetector ? status : 'off' };
  }
}
